import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import Button from '../components/Button';
import { bookTestSlot, checkExistingBooking, getProgram } from '../services/api';
import { getDates } from '../utils/dateUtils';

const PRIMARY = '#6332D1';
const PRIMARY_DARK = '#5228B0';
const PRIMARY_LIGHT = 'rgba(99, 50, 209, 0.08)';

const TEXT = {
  heading: '#251A3E',
  body: '#524B70',
  muted: '#7B7394',
  subtle: '#A09BB5',
  accent: PRIMARY,
};

const PROGRAM_LOGOS = {
  1: '/assets/iitm x cce logo1.png',
  2: '/assets/iit-gandhinagar x cdf logo.png',
  3: '/assets/iitj-logo.jpeg',
};

const TEST_TOPICS = {
  2: [
    {
      title: 'Quantitative Reasoning',
      description: '',
      questions: 30,
      icon: '🧩',
    },
    {
      title: 'Programming Fundamentals',
      description: '',
      questions: 15,
      icon: '📖',
    },
    {
      title: 'Technical Reasoning',
      description: '',
      questions: 15,
      icon: '🔢',
    },
    {
      title: 'Data Interpretation + Logical & Analytical Reasoning',
      description: '',
      questions: 15,
      icon: '🔢',
    },
    {
      title: 'Coding & Problem-Solving',
      description: '',
      questions: 15,
      icon: '🔢',
    },
  ],
  3: [
    {
      title: 'Mathematical Concepts',
      description: 'Arithmetic, percentages, ratios, and data interpretation.',
      questions: 30,
      icon: '🧩',
    },
    {
      title: 'English and Verbal Ability',
      description: 'Reading comprehension, vocabulary, grammar, and verbal logic.',
      questions: 15,
      icon: '📖',
    },
    {
      title: 'Problem Solving',
      description: 'Patterns, sequences, deductions, and analytical problem-solving.',
      questions: 15,
      icon: '🔢',
    },
  ],
};

const getTopicsForProgram = (programId) => TEST_TOPICS[parseInt(programId, 10)] || [];

const PROGRESS_STEPS = [
  'Entrance Test',
  'Secure Seat',
  'Complete Admission',
  'Join Program',
];

const BookSlotContainer = styled.div`
  min-height: 100vh;
  background-color: #F5F3FF;
  overflow-x: hidden;
  color: ${TEXT.body};
`;

const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    padding: 0.75rem 2rem;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 4rem;
  }
`;

const LogoImage = styled.img`
  width: auto;
  height: 48px;
  cursor: pointer;
  max-width: 70vw;
  object-fit: contain;

  @media (min-width: 768px) {
    height: 64px;
    max-width: none;
  }

  @media (min-width: 1024px) {
    height: 80px;
  }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${PRIMARY};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const ProgressBar = styled.div`
  background: linear-gradient(to bottom right, #7c3aed, #6332D1, #4c1d95);
  padding: 0.875rem 0.75rem;
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const ProgressSteps = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 1.5rem;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 0 3rem;
  }

  @media (min-width: 1200px) {
    padding: 0 4rem;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  flex: ${props => props.isLast ? '0 0 auto' : '1'};
  min-width: 0;
`;

const StepCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? PRIMARY : 'white'};
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 0.95rem;
  }
`;

const StepInfo = styled.div`
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  min-width: 0;

  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    margin-left: 0.75rem;
    margin-right: 1rem;
  }
`;

const StepTitle = styled.div`
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.78)'};
  font-weight: ${props => props.active ? '700' : '500'};
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const StepConnector = styled.div`
  flex: 1;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.45);
  margin-right: 0.5rem;
  min-width: 8px;

  @media (min-width: 768px) {
    margin-right: 1rem;
    min-width: 24px;
  }
`;

const MobileActiveStep = styled.div`
  display: none;
  text-align: center;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
  font-size: 0.85rem;
  margin-top: 0.65rem;
  letter-spacing: 0.01em;

  @media (max-width: 767px) {
    display: block;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem 2rem;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 2rem 3rem 3rem;
  }

  @media (min-width: 1200px) {
    padding: 2rem 4rem 3rem;
  }
`;

const ProgramTitle = styled.h1`
  text-align: center;
  font-size: clamp(1.1rem, 4vw, 1.75rem);
  font-weight: 700;
  color: ${TEXT.heading};
  margin-bottom: 1.25rem;
  line-height: 1.45;
  padding: 0 0.25rem;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
    padding: 0;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.hasSidebar ? '1fr minmax(280px, 340px)' : '1fr'};
  gap: 1rem;
  align-items: start;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const BookingSection = styled.div`
  background-color: white;
  border-radius: 12px;
  border: 1px solid #ececf0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  text-align: center;
  color: ${TEXT.heading};
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 1.35rem;
    margin-bottom: 1.5rem;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const ModeToggleTrack = styled.div`
  display: inline-flex;
  background-color: #f3f3f6;
  border-radius: 999px;
  padding: 4px;
  border: 1px solid #e5e5ea;
`;

const ModeOption = styled.button`
  border: none;
  background-color: ${props => props.active ? PRIMARY : 'transparent'};
  color: ${props => props.active ? 'white' : TEXT.muted};
  padding: 0.55rem 1.25rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled && !props.active ? 0.5 : 1};
  transition: all 0.2s ease;
`;

const BookingText = styled.p`
  color: ${TEXT.body};
  font-size: 0.9rem;
  line-height: 1.55;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.75rem;
  }
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DateCard = styled.div`
  padding: 0.75rem;
  border: 1px solid ${props => props.selected ? PRIMARY : '#ddd'};
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  background-color: ${props => props.selected ? PRIMARY_LIGHT : 'white'};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${PRIMARY};
  }
`;

const DateText = styled.div`
  font-weight: ${props => props.selected ? '600' : '500'};
  color: ${props => props.selected ? PRIMARY_DARK : TEXT.body};
  font-size: ${props => props.single ? '1.05rem' : '0.95rem'};

  @media (min-width: 768px) {
    font-size: ${props => props.single ? '1.15rem' : '1rem'};
  }
`;

const DayText = styled.div`
  font-size: ${props => props.single ? '0.9rem' : '0.8rem'};
  color: ${TEXT.muted};
  margin-top: 0.35rem;
`;

const SlotLabel = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${TEXT.heading};
  margin-bottom: 1rem;
  letter-spacing: 0.01em;
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.65rem;
  margin-bottom: 1.5rem;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    margin-bottom: 1.75rem;
  }
`;

const TimeSlot = styled.div`
  padding: 0.65rem 0.35rem;
  border: 1px solid ${props => {
    if (props.disabled) return '#e5e5ea';
    return props.selected ? PRIMARY : PRIMARY;
  }};
  border-radius: 8px;
  text-align: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => {
    if (props.disabled) return '#f8f8fa';
    return props.selected ? PRIMARY : 'white';
  }};
  color: ${props => {
    if (props.disabled) return TEXT.subtle;
    return props.selected ? 'white' : TEXT.body;
  }};
  font-weight: ${props => props.selected ? '600' : '500'};
  font-size: 0.82rem;
  transition: all 0.15s ease;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    padding: 0.7rem 0.5rem;
    font-size: 0.9rem;
    min-height: auto;
  }

  &:hover {
    background-color: ${props => {
      if (props.disabled) return '#f8f8fa';
      return props.selected ? PRIMARY_DARK : PRIMARY_LIGHT;
    }};
    border-color: ${props => props.disabled ? '#e5e5ea' : PRIMARY};
  }
`;

const SlotStatus = styled.div`
  font-size: 0.65rem;
  color: ${props => props.status === 'full' ? PRIMARY : '#ff9800'};
  margin-top: 0.25rem;
`;

const AgreementContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 1.25rem 0 1.5rem;
  gap: 0.65rem;
`;

const AgreementCheckbox = styled.input`
  margin-top: 3px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${PRIMARY};
  flex-shrink: 0;
`;

const AgreementLabel = styled.label`
  font-size: 0.85rem;
  color: ${TEXT.body};
  cursor: pointer;
  line-height: 1.55;

  strong {
    color: ${PRIMARY_DARK};
    font-weight: 600;
  }

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const AgreementLink = styled.a`
  color: ${PRIMARY};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const PaymentButton = styled(Button)`
  margin-top: 0.5rem;
  border-radius: 8px !important;
  background-color: ${PRIMARY} !important;
  color: white !important;
  font-size: 0.78rem !important;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  line-height: 1.35 !important;
  padding: 14px 12px !important;
  white-space: normal !important;
  word-break: break-word;

  @media (min-width: 480px) {
    font-size: 0.85rem !important;
    padding: 12px 16px !important;
  }

  @media (min-width: 768px) {
    font-size: 0.95rem !important;
    letter-spacing: 0.02em;
  }

  &:disabled {
    background-color: #A98FEE !important;
    color: white !important;
    opacity: 1;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: ${PRIMARY_DARK} !important;
    color: white !important;
  }
`;

const TopicsSection = styled.div`
  background-color: white;
  border-radius: 12px;
  border: 1px solid #ececf0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;
  position: static;

  @media (min-width: 961px) {
    padding: 1.5rem;
    position: sticky;
    top: 1rem;
  }
`;

const TopicsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.35rem;
`;

const TopicsTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${TEXT.heading};
  margin: 0;
  letter-spacing: -0.01em;
`;

const DurationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: ${TEXT.muted};
  font-size: 0.85rem;
  font-weight: 500;
`;

const TopicsMeta = styled.p`
  color: ${TEXT.muted};
  font-size: 0.85rem;
  margin: 0 0 1.25rem;

  span {
    color: ${PRIMARY_DARK};
    font-weight: 500;
  }
`;

const TopicCard = styled.div`
  background-color: #F9F7FF;
  border: 1px solid #EBE4F8;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TopicHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
`;

const TopicIcon = styled.span`
  font-size: 1rem;
`;

const TopicName = styled.h4`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${TEXT.heading};
  margin: 0;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const TopicDescription = styled.p`
  font-size: 0.82rem;
  color: ${TEXT.body};
  margin: 0 0 0.5rem;
  line-height: 1.5;
`;

const TopicStats = styled.div`
  font-size: 0.8rem;
  color: ${PRIMARY_DARK};
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: 1rem;
  color: ${TEXT.muted};
  padding: 0 1rem;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BookSlot = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [bookingLoading, setBookingLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [checkingBooking, setCheckingBooking] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [testMode, setTestMode] = useState('online');

  const [weekendDates, setWeekendDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [program, setProgram] = useState(null);

  const user = JSON.parse(localStorage.getItem('user')) || {};

  const today = new Date();
  const currentHour = today.getHours();
  const currentMinute = today.getMinutes();

  const getTimeSlotsForProgram = (programId) => {
    if (parseInt(programId) === 2) {
      return [
        { time: '07:00 PM', status: null }
      ];
    }
    
    // Default time slots for other programs
    return [
      { time: '10:00 AM', status: null },
      { time: '11:00 AM', status: null },
      { time: '12:00 PM', status: 'filling' },
      { time: '01:00 PM', status: 'filling' },
      { time: '02:00 PM', status: 'filling' },
      { time: '03:00 PM', status: 'filling' },
      { time: '04:00 PM', status: 'filling' },
      { time: '05:00 PM', status: 'filling' },
      { time: '06:00 PM', status: null },
      { time: '07:00 PM', status: 'filling' },
      { time: '08:00 PM', status: 'filling' },
      { time: '09:00 PM', status: 'filling' },
      { time: '10:00 PM', status: null },
      { time: '11:00 PM', status: null }
    ];
  };

  const timeSlots = getTimeSlotsForProgram(programId);

  const getAvailableDatesForProgram = (programId) => {
    if (parseInt(programId) === 2) {
      const today = new Date();
      const daysUntilSaturday = (6 - today.getDay() + 7) % 7;
      const firstSaturday = new Date(today);
      firstSaturday.setDate(today.getDate() + daysUntilSaturday);
      return [firstSaturday];
      
    } else if (parseInt(programId) === 3) {
      const today = new Date();
      const daysUntilSunday = (7 - today.getDay()) % 7;
      const firstSunday = new Date(today);
      firstSunday.setDate(today.getDate() + daysUntilSunday);
      return [firstSunday];
    }

    return getDates();
  };

  const isTimeSlotPassed = (timeStr, date) => {
    if (!date || date.getDate() !== today.getDate() ||
        date.getMonth() !== today.getMonth() ||
        date.getFullYear() !== today.getFullYear()) {
      return false;
    }

    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return (hours < currentHour) || (hours === currentHour && minutes <= currentMinute);
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleSelectTime = (time) => {
    setSelectedTime(time);
  };

  const handlePayAndBook = async () => {
    if (!selectedTime || !selectedDate) {
      toast.error('Please select a date and time slot');
      return;
    }

    try {
      setBookingLoading(true);

      const formattedDate = format(selectedDate, 'yyyy-MM-dd') + ' 00:00:00';

      const bookingData = {
        program_id: Number(programId),
        user_id: user.user_id,
        test_date: formattedDate,
        time_slot: selectedTime,
        fee: program?.fee || 49
      };

      await bookTestSlot(bookingData);

    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setBookingLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const programLogo = PROGRAM_LOGOS[parseInt(programId)] || '/assets/fut-logo.png';
  const programHeading = program?.name || 'Schedule Your Entrance Test';
  const isSingleDate = weekendDates.length === 1;
  const programTopics = getTopicsForProgram(programId);

  useEffect(() => {
    const checkBooking = async () => {
      try {
        setCheckingBooking(true);

        if (!user.user_id) {
          navigate('/login');
          return;
        }

        const existingBooking = await checkExistingBooking(user.user_id, programId);

        if (existingBooking) {
          toast.info('You have already booked a test for this program');
          navigate(`/test-success/${existingBooking.booking_id}`);
          return;
        }

        const programData = await getProgram(programId);
        setProgram(programData);

        const dates = getAvailableDatesForProgram(programId);
        setWeekendDates(dates);

        if (dates.length > 0) {
          setSelectedDate(dates[0]);
        }

      } catch (error) {
        console.error('Error checking booking:', error);
      } finally {
        setCheckingBooking(false);
      }
    };

    checkBooking();
  }, [programId, navigate, user.user_id]);

  if (checkingBooking) {
    return (
      <BookSlotContainer>
        <PageHeader>
          <LogoImage src={programLogo} alt="Program logo" onClick={() => navigate('/')} />
          <Avatar>{getInitials(user.name)}</Avatar>
        </PageHeader>
        <Content>
          <LoadingContainer>
            Checking booking status...
          </LoadingContainer>
        </Content>
      </BookSlotContainer>
    );
  }

  return (
    <BookSlotContainer>
      <PageHeader>
        <LogoImage src={programLogo} alt="Program logo" onClick={() => navigate('/')} />
        <Avatar>{getInitials(user.name)}</Avatar>
      </PageHeader>

      <ProgressBar>
        <ProgressSteps>
          {PROGRESS_STEPS.map((label, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === 1;
            const isLast = index === PROGRESS_STEPS.length - 1;

            return (
              <ProgressStep key={label} isLast={isLast}>
                <StepCircle active={isActive}>{stepNumber}</StepCircle>
                <StepInfo>
                  <StepTitle active={isActive}>{label}</StepTitle>
                </StepInfo>
                {!isLast && <StepConnector />}
              </ProgressStep>
            );
          })}
        </ProgressSteps>
        <MobileActiveStep>Step 1: Entrance Test</MobileActiveStep>
      </ProgressBar>

      <Content>
        <ProgramTitle>{programHeading}</ProgramTitle>

        <MainGrid hasSidebar={programTopics.length > 0}>
          <BookingSection>
            <SectionTitle>Schedule Your Entrance Test</SectionTitle>

            <BookingText>
              Select a date and time slot for your qualifier test. After payment, your test will be scheduled.
            </BookingText>

            <SlotLabel>Select a date</SlotLabel>
            <DateGrid single={isSingleDate}>
              {weekendDates.map((date, index) => (
                <DateCard
                  key={index}
                  single={isSingleDate}
                  selected={selectedDate && selectedDate.getTime() === date.getTime()}
                  onClick={() => handleSelectDate(date)}
                >
                  <DateText
                    single={isSingleDate}
                    selected={selectedDate && selectedDate.getTime() === date.getTime()}
                  >
                    {format(date, 'MMMM d, yyyy')}
                  </DateText>
                  <DayText single={isSingleDate}>
                    {format(date, 'EEEE')}
                  </DayText>
                </DateCard>
              ))}
            </DateGrid>

            <SlotLabel>Select a time slot</SlotLabel>
            <SlotGrid>
              {timeSlots.map((slot, index) => {
                const isPassed = isTimeSlotPassed(slot.time, selectedDate);
                const isFull = slot.status === 'full';
                const isDisabled = isPassed || isFull;

                return (
                  <TimeSlot
                    key={index}
                    selected={selectedTime === slot.time}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleSelectTime(slot.time)}
                  >
                    {slot.time}
                    {slot.status && <SlotStatus status={slot.status}>{slot.status}</SlotStatus>}
                    {isPassed && <SlotStatus status="full">unavailable</SlotStatus>}
                  </TimeSlot>
                );
              })}
            </SlotGrid>

            <AgreementContainer>
              <AgreementCheckbox
                type="checkbox"
                id="terms-agreement"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <AgreementLabel htmlFor="terms-agreement">
                I agree that the fee of <strong>₹{program?.fee || 49}</strong> is non-refundable.
              </AgreementLabel>
            </AgreementContainer>

            <PaymentButton
              size="large"
              onClick={handlePayAndBook}
              disabled={bookingLoading || !selectedTime || !selectedDate || !agreedToTerms}
              fullWidth
            >
              {bookingLoading
                ? 'Processing...'
                : `Pay ₹${program?.fee || 49} and book your test slot`}
            </PaymentButton>
          </BookingSection>

          {programTopics.length > 0 && (
          <TopicsSection>
            <TopicsHeader>
              <TopicsTitle>Test Topics</TopicsTitle>
            </TopicsHeader>
            <TopicsMeta>
              {programTopics.length} sections • <span>online</span>
            </TopicsMeta>

            {programTopics.map((topic) => (
              <TopicCard key={topic.title}>
                <TopicHeader>
                  <TopicIcon>{topic.icon}</TopicIcon>
                  <TopicName>{topic.title}</TopicName>
                </TopicHeader>
                <TopicDescription>{topic.description}</TopicDescription>
                {topic.questions && (
                  <TopicStats>
                    {topic.questions} questions
                  </TopicStats>
                )}
              </TopicCard>
            ))}
          </TopicsSection>
          )}
        </MainGrid>
      </Content>
    </BookSlotContainer>
  );
};

export default BookSlot;
